// Firestore cache for a whole lookup — filings *and* prices.
//
// Reading a ticker's filings costs several SEC round trips per filing, all of them
// funnelled through one 10-requests/second budget shared by everyone using the tool.
// The filings themselves never change once submitted, so caching them is both free
// accuracy and the main thing keeping us a polite SEC citizen.
//
// Prices are cached alongside them, which is a deliberate reversal of this file's
// earlier stance that "yesterday's close would be a stale chart". Over a seven-year
// window it is the shape of the series that is being read, and that shape does not
// meaningfully differ between one Friday and the next. Paying a full re-pull to move
// the right-hand edge a few days is not a trade worth making by default. What the
// staleness costs is stated in the UI instead: a cached lookup shows the date it was
// built and offers a Refresh that forces a live re-pull.
//
// The consequence to keep in mind: within the TTL a *newly filed* 10-Q will not
// appear, and the last data point is up to TTL_MS old. Refresh is the answer to both.
//
// One thing is never cached: a lookup whose split history failed to load. Splits are
// load-bearing, and persisting a failed one would spread a momentary outage across a
// week and every user. See `writeLookupCache`.

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { DEV_MODE } from './api';

// The collection name is unchanged from when this held filings only.
//
// Access is granted by the merged ruleset for the shared `short-triggers` database,
// which lives in ~/CVP/cap-watch/firestore.rules — not by the firestore.rules in this
// repo, which is a pointer and is never deployed. This app and Cap Watch are the only
// two on that project that touch Firestore from the browser, so they are the only two
// that need client rules at all — the rest go through the Admin SDK, which bypasses
// rules. Until that block existed (Jul 2026) every read and write here was denied and
// this cache did nothing at all. If reads start failing again, check the live ruleset
// first; the failure mode is a console warning and a slow lookup, not an error anyone
// sees.
const COLLECTION = 'filingCache';

/** How long a cached lookup is reused before it is rebuilt from SEC and Massive. */
export const TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Firestore's hard ceiling is 1 MiB per document. Stay well under it: a 20-year
// window is ~5,000 bars, and a doc that fails to write is worse than one never
// attempted, because the failure surfaces only as a console warning.
const MAX_CACHE_BYTES = 700 * 1024;

/**
 * Only `t` and `c` are ever read off a bar (`barDate` and the close, both in
 * `marketCap.js`) — open/high/low/volume are ~3× the payload for nothing. Slimming
 * on write is what keeps a 20-year lookup inside the document limit.
 */
function slimBars(bars) {
  return (bars ?? []).map((b) => ({ t: b.t, c: b.c }));
}

/**
 * A cached lookup for a ticker, or null.
 *
 * A cache entry is only usable when it reaches at least as far back as this request
 * needs — a 2-year lookup can be served from a 10-year fetch, but not the reverse.
 * The caller re-derives its own `windowStart` from the years it asked for, so the
 * extra history in a longer entry is filtered out at render time.
 */
export async function readLookupCache(ticker, years) {
  if (DEV_MODE) return null;
  try {
    const snap = await getDoc(doc(db, COLLECTION, ticker));
    if (!snap.exists()) return null;

    const data = snap.data();
    if (!(data.years >= years)) return null;
    if (!data.cachedAtMs || Date.now() - data.cachedAtMs > TTL_MS) return null;

    // Entries written before prices were cached hold filings but no `prices`. They
    // can't serve a lookup on their own, and treating them as a miss rebuilds them
    // in the current shape on first use.
    if (!data.filings || !data.prices?.bars?.length) return null;

    // Backstop to the same check on write. Nothing should be able to store an entry
    // with failed splits, but serving one would silently misstate every market cap
    // before a split date, so it is worth being wrong about twice rather than never.
    if (data.prices.splitsAvailable === false) return null;

    return { filings: data.filings, prices: data.prices, cachedAtMs: data.cachedAtMs };
  } catch (err) {
    // A cache miss must never fail a lookup — fall through to a live fetch.
    console.warn('[cache] read failed:', err.message);
    return null;
  }
}

export async function writeLookupCache(ticker, years, { filings, prices }) {
  if (DEV_MODE) return;
  try {
    // Never persist a lookup whose splits failed to load. `fetchSplits` swallows the
    // failure and returns an empty list, which is indistinguishable from "never
    // split" — so a cached one would put filed share counts on the wrong basis for a
    // week, for everyone, off a single transient Massive outage. Live, that same
    // outage costs one lookup and the next one recovers; caching it is what turns a
    // blip into a week. It would also let a Refresh mid-outage overwrite a good entry.
    //
    // Filing-level errors are deliberately NOT treated this way: a filing that can't
    // be parsed won't parse next week either, so caching it is caching the truth.
    // A split lookup that failed is infrastructure, not a fact about the company.
    if (prices?.splitsAvailable === false) {
      console.warn(`[cache] ${ticker} not cached — split history unavailable`);
      return;
    }

    const payload = {
      years,
      filings,
      prices: { ...prices, bars: slimBars(prices.bars) },
      cachedAtMs: Date.now(),
      cachedAt: serverTimestamp(),
    };

    // Rough but sufficient: if the slimmed payload is still oversized, skip the write
    // rather than let Firestore reject it. The lookup itself already succeeded; not
    // caching it only costs the next reader some time.
    const approxBytes = JSON.stringify({ ...payload, cachedAt: null }).length;
    if (approxBytes > MAX_CACHE_BYTES) {
      console.warn(`[cache] ${ticker} too large to cache (~${Math.round(approxBytes / 1024)} KB)`);
      return;
    }

    await setDoc(doc(db, COLLECTION, ticker), payload);
  } catch (err) {
    console.warn('[cache] write failed:', err.message);
  }
}

// --- Recently viewed tickers (localStorage; per-browser, no server round trip) ---

const RECENT_KEY = 'recentTickers';
const RECENT_LIMIT = 12;

export function readRecentTickers() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter((t) => typeof t === 'string') : [];
  } catch {
    return [];
  }
}

export function pushRecentTicker(ticker) {
  const next = [ticker, ...readRecentTickers().filter((t) => t !== ticker)].slice(0, RECENT_LIMIT);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Private browsing / quota — recents are a nicety, not worth surfacing.
  }
  return next;
}
